import { Component, effect, input, signal, untracked, ViewChild, ViewChildren, QueryList } from '@angular/core';
import { take } from 'rxjs/operators';
import {
  PatientTargetModel,
  GraphDataModel,
  GraphGrouping,
  TargetGraphType,
  TargetType,
  PatientBehaviorModel,
  BehaviorGraphType,
  DataTakerStaffDataModel,
  GraphAnnotationModel,
  PatientBehaviorType,
} from '../../../core/api/models';
import { DatePipe } from '@angular/common';
import { MultiSelectChangeEvent } from 'primeng/multiselect';
import {
  GraphApiService,
  StaffApiService,
  SupervisorApiService,
} from '../../../core/api/services';
import { AlertService } from '../../../shared/services/alert.service';
import { Utils } from '../../../core/utils';
import { ChartOptions, GraphsBaseComponent } from '../../graphs-base.component';
import { ChartComponent } from 'ng-apexcharts';
import { SeriesDataLabelOption, TrendLineDataLabelOption, AnnotationLabelBackgroundOption } from '../../graphs-base.component';
import { AuthService } from '../../../auth/auth.service';
import { Permissions } from '../../../core/api/models';
import { PrintService, PrintHeaderInfo } from '../../../shared/services/print.service';

@Component({
  selector: 'app-behavior-graph',
  templateUrl: './behavior-graph.component.html',
  styleUrl: './behavior-graph.component.css',
  standalone: false,
})
export class BehaviorGraphComponent extends GraphsBaseComponent {
  @ViewChild('chart', { static: false }) chart: ChartComponent | undefined;
  @ViewChildren(ChartComponent) chartList!: QueryList<ChartComponent>;

  showAddAnnotation = signal<boolean>(false);
  annotationToEdit = signal< GraphAnnotationModel | null>( null);

  patientID = input.required<string>();
  patientIDAsNumber: number | null = null;

  startDate = signal<Date>(Utils.dateInPastBy(90));
  endDate = signal<Date>(new Date());
  forceGraphPeriod = signal<boolean>(true);

  isLoading: boolean = false;
  private isLoadingData: boolean = false; // Guard to prevent multiple simultaneous calls

  selectedBehavior = input.required<PatientBehaviorModel | null>();

  staffMembers = signal<DataTakerStaffDataModel[]>([]);
  selectedStaffMember: DataTakerStaffDataModel | null = null;
  loadedStaffMembers = signal<boolean>(false);

  graphData = signal<GraphDataModel | null>(null);

  graphTypes: any[] = [];
  selectedGraphType: BehaviorGraphType | null = null;

  seriesDataLabelOption = signal<SeriesDataLabelOption>(SeriesDataLabelOption.DotsAndValues);
  trendLineDataLabelOption = signal<TrendLineDataLabelOption>(TrendLineDataLabelOption.LineOnly);
  
  seriesDataLabelOptions = [
    { label: SeriesDataLabelOption.LineOnly, value: SeriesDataLabelOption.LineOnly },
    { label: SeriesDataLabelOption.Dots, value: SeriesDataLabelOption.Dots },
    { label: SeriesDataLabelOption.DotsAndValues, value: SeriesDataLabelOption.DotsAndValues },
    { label: SeriesDataLabelOption.FirstAndLastValues, value: SeriesDataLabelOption.FirstAndLastValues },
  ];
  
  trendLineDataLabelOptions = [
    { label: TrendLineDataLabelOption.LineOnly, value: TrendLineDataLabelOption.LineOnly },
    { label: TrendLineDataLabelOption.Dots, value: TrendLineDataLabelOption.Dots },
    { label: TrendLineDataLabelOption.DotsAndValues, value: TrendLineDataLabelOption.DotsAndValues },
    { label: TrendLineDataLabelOption.FirstAndLastValues, value: TrendLineDataLabelOption.FirstAndLastValues },
  ];
  displayLegend = signal<boolean>(true);

  displayTrendLine = signal<boolean>(false);
  segmentTrendLineByPhase = signal<boolean>(false);

  showNotTrackedAsZeroes = signal<boolean>(false);
  showUnusedScaleValues = signal<boolean>(true);

  private dateChangeTimer: any;

  isPrintingPdf = false;
  isPrintingPng = false;

  constructor(
    private graphApiService: GraphApiService,
    private supervisorApiService: SupervisorApiService,
    private staffApiService: StaffApiService,
    private datePipe: DatePipe,
    private alertService: AlertService,
    private authService: AuthService,
    private printService: PrintService
  ) {
    super();
    this.graphExportService = printService;
    this.loadDefaultGraphOptions();

    // Set default date option to 3 months (index 6)
    this.dateOption = this.dateOptions[6].value;

    // Track only the behavior ID to avoid multiple effect runs when other properties change
    effect(() => {
      const behaviorID = this.selectedBehavior()?.ID;
      if (behaviorID !== undefined) {
        untracked(() => {
          this.onSelectedBehaviorChange();
        });
      }
    });
  }

  ngOnInit(): void {
    this.patientIDAsNumber = Number(this.patientID());
    // Don't load staff members here - let the effect handle it when behavior is selected
    // This prevents duplicate calls when behavior is first selected
  }

  onSelectedBehaviorChange() {
    console.log('behaviorId changed to:', this.selectedBehavior()?.ID);
    this.selectedGraphType = null;

    // update groupingOptions and selectedGroupingOption according to the selected behavior
    if (this.selectedBehavior()?.BehaviorType == PatientBehaviorType.Duration ||
      this.selectedBehavior()?.BehaviorType == PatientBehaviorType.Intensity) {
      this.groupingOptions = this.groupingOptionsIncludingStaffOptionAndByTrial;
    } else {
      this.groupingOptions = this.groupingOptionsIncludingStaffOption;
    }

    // Clear staff members to force reload when behavior changes
    this.staffMembers.set([]);
    this.loadData();
  }

  loadData() {
    this.loadDataForGraph();
  }

  onGraphTypeChange(event: any) {
    this.selectedGraphType = event.value;
    this.loadDataForGraph();
  }

  hasSupervisorPermission(): boolean {
    const userInfo = this.authService.userInfo;
    if (!userInfo || !userInfo.Permissions) {
      return false;
    }
    return userInfo.Permissions.includes(Permissions.SparkApp_Supervisor);
  }

  shouldDisplayAddAnnotationButton(): boolean {
    return this.hasSupervisorPermission();
  }

  updateAvailableGraphTypes() {
    // set the available graph types based on the target type
    switch (this.selectedBehavior()?.BehaviorType) {
      case PatientBehaviorType.Duration:
        this.graphTypes = [
          {
            text: 'Frequency',
            value: BehaviorGraphType.Frequency,
          },
          {
            text: 'Duration Total',
            value: BehaviorGraphType.DurationTotal,
          },
          {
            text: 'Duration Average',
            value: BehaviorGraphType.DurationAverage,
          },
          {
            text: 'Duration Trials - Time of Day',
            value: BehaviorGraphType.DurationTrialsTimeOfDay,
          },
          {
            text: 'Duration By Trial',
            value: BehaviorGraphType.DurationByTrial,
          },
          { text: 'Time of Day', value: BehaviorGraphType.TimeOfDay },
          {
            text: 'Antecedents for Behavior',
            value: BehaviorGraphType.AntecedentsForBehavior,
          },
          {
            text: 'Consequences for Behavior',
            value: BehaviorGraphType.ConsequencesForBehavior,
          },
          {
            text: 'Locations for Behavior',
            value: BehaviorGraphType.LocationsForBehavior,
          },
          {
            text: 'Responses for Behavior',
            value: BehaviorGraphType.ResponseForBehavior,
          },
          {
            text: 'Severities for Behavior',
            value: BehaviorGraphType.SeverityForBehavior,
          },
          {
            text: 'Frequency for Antecedents',
            value: BehaviorGraphType.FrequencyForAntecedents,
          },
          {
            text: 'Frequency for Consequences',
            value: BehaviorGraphType.FrequencyForConsequences,
          },
          {
            text: 'Frequency for Locations',
            value: BehaviorGraphType.FrequencyForLocations,
          },
          {
            text: 'Frequency for Responses',
            value: BehaviorGraphType.FrequencyForResponses,
          },
          {
            text: 'Frequency for Severities',
            value: BehaviorGraphType.FrequencyForSeverities,
          },
        ];
        if (this.selectedGraphType == null) {
          this.selectedGraphType = BehaviorGraphType.DurationTotal;
        }
        break;
      case PatientBehaviorType.Frequency:
        this.graphTypes = [
          {
            text: 'Frequency',
            value: BehaviorGraphType.Frequency,
          },
          { text: 'Time of Day', value: BehaviorGraphType.TimeOfDay },
          {
            text: 'Antecedents for Behavior',
            value: BehaviorGraphType.AntecedentsForBehavior,
          },
          {
            text: 'Consequences for Behavior',
            value: BehaviorGraphType.ConsequencesForBehavior,
          },
          {
            text: 'Locations for Behavior',
            value: BehaviorGraphType.LocationsForBehavior,
          },
          {
            text: 'Responses for Behavior',
            value: BehaviorGraphType.ResponseForBehavior,
          },
          {
            text: 'Severities for Behavior',
            value: BehaviorGraphType.SeverityForBehavior,
          },
          {
            text: 'Frequency for Antecedents',
            value: BehaviorGraphType.FrequencyForAntecedents,
          },
          {
            text: 'Frequency for Consequences',
            value: BehaviorGraphType.FrequencyForConsequences,
          },
          {
            text: 'Frequency for Locations',
            value: BehaviorGraphType.FrequencyForLocations,
          },
          {
            text: 'Frequency for Responses',
            value: BehaviorGraphType.FrequencyForResponses,
          },
          {
            text: 'Frequency for Severities',
            value: BehaviorGraphType.FrequencyForSeverities,
          },
        ];
        if (this.selectedGraphType == null) {
          this.selectedGraphType = BehaviorGraphType.Frequency;
        }
        break;
      case PatientBehaviorType.Interval:
        this.graphTypes = [
          {
            text: 'Interval % Happened',
            value: BehaviorGraphType.IntervalPercentageHappened,
          },
          {
            text: 'Interval % Not Happened',
            value: BehaviorGraphType.IntervalPercentageNotHappened,
          },
          { text: 'Time of Day', value: BehaviorGraphType.TimeOfDay },
          {
            text: 'Antecedents for Behavior',
            value: BehaviorGraphType.AntecedentsForBehavior,
          },
          {
            text: 'Consequences for Behavior',
            value: BehaviorGraphType.ConsequencesForBehavior,
          },
          {
            text: 'Locations for Behavior',
            value: BehaviorGraphType.LocationsForBehavior,
          },
          {
            text: 'Responses for Behavior',
            value: BehaviorGraphType.ResponseForBehavior,
          },
          {
            text: 'Severities for Behavior',
            value: BehaviorGraphType.SeverityForBehavior,
          },
          {
            text: 'Frequency for Antecedents',
            value: BehaviorGraphType.FrequencyForAntecedents,
          },
          {
            text: 'Frequency for Consequences',
            value: BehaviorGraphType.FrequencyForConsequences,
          },
          {
            text: 'Frequency for Locations',
            value: BehaviorGraphType.FrequencyForLocations,
          },
          {
            text: 'Frequency for Responses',
            value: BehaviorGraphType.FrequencyForResponses,
          },
          {
            text: 'Frequency for Severities',
            value: BehaviorGraphType.FrequencyForSeverities,
          },
        ];
        if (this.selectedGraphType == null) {
          this.selectedGraphType = BehaviorGraphType.IntervalPercentageHappened;
        }
        break;
      case PatientBehaviorType.RateTimer:
        this.graphTypes = [
          {
            text: 'Occurences',
            value: BehaviorGraphType.RateTimerLine,
          },
          { text: 'Time of Day', value: BehaviorGraphType.TimeOfDay },
          {
            text: 'Antecedents for Behavior',
            value: BehaviorGraphType.AntecedentsForBehavior,
          },
          {
            text: 'Consequences for Behavior',
            value: BehaviorGraphType.ConsequencesForBehavior,
          },
          {
            text: 'Locations for Behavior',
            value: BehaviorGraphType.LocationsForBehavior,
          },
          {
            text: 'Responses for Behavior',
            value: BehaviorGraphType.ResponseForBehavior,
          },
          {
            text: 'Severities for Behavior',
            value: BehaviorGraphType.SeverityForBehavior,
          },
          {
            text: 'Frequency for Antecedents',
            value: BehaviorGraphType.FrequencyForAntecedents,
          },
          {
            text: 'Frequency for Consequences',
            value: BehaviorGraphType.FrequencyForConsequences,
          },
          {
            text: 'Frequency for Locations',
            value: BehaviorGraphType.FrequencyForLocations,
          },
          {
            text: 'Frequency for Responses',
            value: BehaviorGraphType.FrequencyForResponses,
          },
          {
            text: 'Frequency for Severities',
            value: BehaviorGraphType.FrequencyForSeverities,
          },
        ];
        if (this.selectedGraphType == null) {
          this.selectedGraphType = BehaviorGraphType.RateTimerLine;
        }
        break;
      case PatientBehaviorType.Intensity:
        this.graphTypes = [
          {
            text: 'Intensity',
            value: BehaviorGraphType.IntensityBar,
          },
          {
            text: 'Intensity Over Time',
            value: BehaviorGraphType.IntensityOverTime,
          },
          { text: 'Time of Day', value: BehaviorGraphType.TimeOfDay },
          {
            text: 'Antecedents for Behavior',
            value: BehaviorGraphType.AntecedentsForBehavior,
          },
          {
            text: 'Consequences for Behavior',
            value: BehaviorGraphType.ConsequencesForBehavior,
          },
          {
            text: 'Locations for Behavior',
            value: BehaviorGraphType.LocationsForBehavior,
          },
          {
            text: 'Responses for Behavior',
            value: BehaviorGraphType.ResponseForBehavior,
          },
          {
            text: 'Severities for Behavior',
            value: BehaviorGraphType.SeverityForBehavior,
          },
          {
            text: 'Frequency for Antecedents',
            value: BehaviorGraphType.FrequencyForAntecedents,
          },
          {
            text: 'Frequency for Consequences',
            value: BehaviorGraphType.FrequencyForConsequences,
          },
          {
            text: 'Frequency for Locations',
            value: BehaviorGraphType.FrequencyForLocations,
          },
          {
            text: 'Frequency for Responses',
            value: BehaviorGraphType.FrequencyForResponses,
          },
          {
            text: 'Frequency for Severities',
            value: BehaviorGraphType.FrequencyForSeverities,
          },
        ];
        if (this.selectedGraphType == null) {
          this.selectedGraphType = BehaviorGraphType.IntensityBar;
        }
        break;
      default:
        this.graphTypes = [
          {
            text: 'Frequency',
            value: BehaviorGraphType.Frequency,
          },
          {
            text: 'Antecedents for Behavior',
            value: BehaviorGraphType.AntecedentsForBehavior,
          },
          {
            text: 'Consequences for Behavior',
            value: BehaviorGraphType.ConsequencesForBehavior,
          },
          {
            text: 'Locations for Behavior',
            value: BehaviorGraphType.LocationsForBehavior,
          },
          {
            text: 'Responses for Behavior',
            value: BehaviorGraphType.ResponseForBehavior,
          },
          {
            text: 'Severities for Behavior',
            value: BehaviorGraphType.SeverityForBehavior,
          },
          {
            text: 'Frequency for Antecedents',
            value: BehaviorGraphType.FrequencyForAntecedents,
          },
          {
            text: 'Frequency for Consequences',
            value: BehaviorGraphType.FrequencyForConsequences,
          },
          {
            text: 'Frequency for Locations',
            value: BehaviorGraphType.FrequencyForLocations,
          },
          {
            text: 'Frequency for Responses',
            value: BehaviorGraphType.FrequencyForResponses,
          },
          {
            text: 'Frequency for Severities',
            value: BehaviorGraphType.FrequencyForSeverities,
          },
        ];
        if (this.selectedGraphType == null) {
          this.selectedGraphType = BehaviorGraphType.Frequency;
        }
        break;
    }
  }

  updateAvailableGraphGroupingOptions() {
    const includeByTrial =
      this.selectedBehavior()?.BehaviorType == PatientBehaviorType.Duration ||
      (this.selectedBehavior()?.BehaviorType == PatientBehaviorType.Intensity &&
        this.selectedGraphType === BehaviorGraphType.IntensityOverTime);
    if (this.staffMembers().length > 1) {
      if (includeByTrial) {
        this.groupingOptions = this.groupingOptionsIncludingStaffOptionAndByTrial;
      } else {
        this.groupingOptions = this.groupingOptionsIncludingStaffOption;
      }
    } else {
      if (includeByTrial) {
        this.groupingOptions = this.groupingOptionsExcludingStaffOptionWithByTrial;
      } else {
        this.groupingOptions = this.groupingOptionsExcludingStaffOption;
      }
    }
  }

  loadDataForGraph() {
    // Prevent multiple simultaneous calls
    if (this.isLoadingData) {
      console.log('loadDataForGraph: Already loading, skipping duplicate call');
      return;
    }

    this.updateAvailableGraphTypes();
    this.updateAvailableGraphGroupingOptions();

    var behaviorID = this.selectedBehavior()?.ID;

    var startTime = this.startDate();
    var endTime = this.endDate();

    // Ensure staff list is loaded before fetching the graph (needed for tooltips)
    if (this.staffMembers().length === 0 && !this.loadedStaffMembers()) {
      this.graphApiService.GetDataTakerStaffsForBehaviorID({ behaviorID: behaviorID! }).subscribe(
        (staffMembers) => { 
          this.staffMembers.set(staffMembers); 
          this.loadedStaffMembers.set(true);
          this.loadDataForGraph(); 
        },
        (error) => {
          console.error('Error fetching data taker staffs for behavior', error);
          this.alertService.error(
            'Error fetching data taker staffs for behavior.'
          );
          this.staffMembers.set([]);
          this.loadedStaffMembers.set(true);
          this.loadDataForGraph();
        }
      );
      return;
    }

    this.isLoading = true;
    this.graphApiService
      .GetBehaviorGraph({
        patientID: Number(this.patientID()),
        behaviorID: behaviorID!,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        graphType: this.selectedGraphType!,
        graphGrouping: this.groupingOption,
        staffID: this.selectedStaffMember?.StaffID,
        includeTrendLine: this.displayTrendLine(),
        segmentTrendLineByPhase: this.displayTrendLine() && this.segmentTrendLineByPhase(),
        showNotTrackedAsZeroes: this.showNotTrackedAsZeroes(),
      })
      .subscribe(
        (data) => {
          console.log(data);

          this.staffMembers.set(data.DataTakers ?? []);

          // do the correction only for the necessary graph types
          switch (this.selectedGraphType) {
            case BehaviorGraphType.Frequency:
            case BehaviorGraphType.DurationTotal:
            case BehaviorGraphType.DurationAverage:
            case BehaviorGraphType.FrequencyForAntecedents:
            case BehaviorGraphType.FrequencyForConsequences:
            case BehaviorGraphType.FrequencyForLocations:
            case BehaviorGraphType.FrequencyForResponses:
            case BehaviorGraphType.FrequencyForSeverities:
            case BehaviorGraphType.IntervalPercentageHappened:
            case BehaviorGraphType.IntervalPercentageNotHappened:
            case BehaviorGraphType.DurationByTrial:
            case BehaviorGraphType.DurationTrialsTimeOfDay:
            case BehaviorGraphType.RateTimerLine:
            case BehaviorGraphType.IntensityOverTime:
              // Add placeholder data points for the start and end of the selected period
              if (this.forceGraphPeriod()) {
                const startDate = this.startDate().toServerString();
                const endDate = this.endDate().toServerString();

                if (data.Series && data.Series.length > 0) {
                  for (const series of data.Series) {
                    if (series.DataPoints && series.DataPoints.length > 0) {
                      const firstDataPoint = series.DataPoints[0];
                      const lastDataPoint =
                        series.DataPoints[series.DataPoints.length - 1];

                      if (firstDataPoint.Date !== startDate) {
                        series.DataPoints.unshift({
                          Date: startDate,
                          Value: null,
                        });
                      }

                      if (lastDataPoint.Date !== endDate) {
                        series.DataPoints.push({ Date: endDate, Value: null });
                      }
                    } else {
                      // If no points in series, add placeholders for the entire period
                      series.DataPoints = [
                        { Date: startDate, Value: null },
                        { Date: endDate, Value: null },
                      ];
                    }
                  }
                }
              }
              break;
            default:
              break;
          }

          this.graphData.set(this.filterIntensityBarSeries(data));
          this.annotations.set(data.Annotations ?? []);
          this.updateAnnotationDisplayGroupCheckmarks();
          this.updateGraph();
          this.isLoading = false;
          this.isLoadingData = false;
        },
        (error) => {
          console.error('Error fetching data', error);
          this.alertService.error(
            Utils.getErrorMessageFromErrorResponse(error)
          );
          this.graphData.set(null);
          this.updateGraph();
          this.isLoading = false;
          this.isLoadingData = false;
        }
      );
  }

  shouldDisplayGraphTypeOptions(): boolean {
    return true;
  }

  shouldDisplayTrendLineOption(): boolean {
    return this.selectedGraphType === BehaviorGraphType.Frequency ||
      this.selectedGraphType === BehaviorGraphType.DurationTotal ||
      this.selectedGraphType === BehaviorGraphType.DurationAverage ||
      this.selectedGraphType === BehaviorGraphType.DurationByTrial ||
      this.selectedGraphType === BehaviorGraphType.IntervalPercentageHappened ||
      this.selectedGraphType === BehaviorGraphType.IntervalPercentageNotHappened ||
      this.selectedGraphType === BehaviorGraphType.IntensityOverTime;
  }

  shouldDisplayGroupingOptions(): boolean {
    if (this.selectedGraphType === BehaviorGraphType.AntecedentsForBehavior ||
      this.selectedGraphType === BehaviorGraphType.ConsequencesForBehavior ||
      this.selectedGraphType === BehaviorGraphType.LocationsForBehavior ||
      this.selectedGraphType === BehaviorGraphType.ResponseForBehavior ||
      this.selectedGraphType === BehaviorGraphType.SeverityForBehavior ||
      this.selectedGraphType === BehaviorGraphType.IntensityBar ||
      this.selectedGraphType === BehaviorGraphType.DurationByTrial) {
        return false;
      }
    return true;
  }

  shouldDisplayStaffMemberOptions(): boolean {
    return this.staffMembers().length > 1 && this.groupingOption != GraphGrouping.ByStaffMember;
  }

  shouldDisplayDisplayOptions(): boolean {
    return this.selectedGraphType === BehaviorGraphType.Frequency ||
      this.selectedGraphType === BehaviorGraphType.DurationTotal ||
      this.selectedGraphType === BehaviorGraphType.DurationAverage ||
      this.selectedGraphType === BehaviorGraphType.DurationByTrial ||
      this.selectedGraphType === BehaviorGraphType.FrequencyForAntecedents ||
      this.selectedGraphType === BehaviorGraphType.FrequencyForConsequences ||
      this.selectedGraphType === BehaviorGraphType.FrequencyForLocations ||
      this.selectedGraphType === BehaviorGraphType.FrequencyForResponses ||
      this.selectedGraphType === BehaviorGraphType.FrequencyForSeverities ||
      this.selectedGraphType === BehaviorGraphType.IntervalPercentageHappened ||
      this.selectedGraphType === BehaviorGraphType.IntervalPercentageNotHappened ||
      this.selectedGraphType === BehaviorGraphType.IntensityOverTime;
  }

  shouldDisplayGraph(): boolean {
    return (
      this.isLoading === false &&
      this.selectedBehavior() != null &&
      !this.isGraphEmpty()
    );
  }

  shouldDisplayToggleGrouped(): boolean {
    return false;
  }

  shouldDisplayShowNotTrackedAsZeroes(): boolean {
    if (this.selectedGraphType === BehaviorGraphType.AntecedentsForBehavior ||
      this.selectedGraphType === BehaviorGraphType.ConsequencesForBehavior ||
      this.selectedGraphType === BehaviorGraphType.LocationsForBehavior ||
      this.selectedGraphType === BehaviorGraphType.ResponseForBehavior ||
      this.selectedGraphType === BehaviorGraphType.SeverityForBehavior ||
      this.selectedGraphType === BehaviorGraphType.FrequencyForResponses ||
      this.selectedGraphType === BehaviorGraphType.FrequencyForSeverities ||
      this.selectedGraphType === BehaviorGraphType.FrequencyForAntecedents ||
      this.selectedGraphType === BehaviorGraphType.FrequencyForConsequences ||
      this.selectedGraphType === BehaviorGraphType.FrequencyForLocations) {
        return false;
      }
    if (this.selectedGraphType === BehaviorGraphType.IntensityBar) {
      return false;
    }
    return true;
  }

  isGraphEmpty(): boolean {
    // check if the graph data is empty
    if (this.graphData() == null) {
      return true;
    }

    // check if series is empty
    if (this.graphData()?.Series?.length == 0) {
      return true;
    }

    // for some graph types, check the lenght of the first series as well.
    switch (this.selectedGraphType) {
      case BehaviorGraphType.Frequency:
      case BehaviorGraphType.DurationTotal:
      case BehaviorGraphType.DurationAverage:
      case BehaviorGraphType.FrequencyForAntecedents:
      case BehaviorGraphType.FrequencyForConsequences:
      case BehaviorGraphType.FrequencyForLocations:
      case BehaviorGraphType.FrequencyForResponses:
      case BehaviorGraphType.FrequencyForSeverities:
        return this.graphData()!.Series![0].DataPoints!.length == 0;

      default:
        return false;
    }
  }

  formatDateForInput(date: Date): string {
    return Utils.formatDateForInput(date);
  }

  onStartDateChange(event: any) {
    if (!event) return;
    
    // Clear any existing timer
    if (this.dateChangeTimer) {
      clearTimeout(this.dateChangeTimer);
    }
    
    // Set new timer
    this.dateChangeTimer = setTimeout(() => {
      const date = Utils.parseDateFromInput(event);
      this.startDate.set(date);
      this.dateOption = this.dateOptions[0].value; // Custom option value
      this.loadDataForGraph();
    }, 1500); // Wait 1.5 seconds after last change
  }

  onEndDateChange(event: any) {
    if (!event) return;
    
    // Clear any existing timer
    if (this.dateChangeTimer) {
      clearTimeout(this.dateChangeTimer);
    }
    
    // Set new timer
    this.dateChangeTimer = setTimeout(() => {
      const date = Utils.parseDateFromInput(event);
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);
      this.endDate.set(endOfDay);
      this.dateOption = this.dateOptions[0].value; // Custom option value
      this.loadDataForGraph();
    }, 1500); // Wait 1.5 seconds after last change
  }

  onDateChange(event: any) {
    const selectedDate = event.target.valueAsDate;
    // date is coming in UTC, we need to convert it to local time
    const localDate = new Date(selectedDate.getTime() + selectedDate.getTimezoneOffset() * 60000);
    const localDateEndOfDay = new Date(localDate.getTime() + 23 * 60 * 60 * 1000 + 59 * 60 * 1000 + 59 * 1000 + 999);

    this.startDate.set(localDate);
    this.endDate.set(localDateEndOfDay);
    //this.loadDataForGraph();
  }

  onDateOptionChange(event: any) {
    // Don't update if custom is selected
    if (event.value === this.dateOptions[0].value) {
      return;
    }
    
    // Update start date based on the selected option
    this.startDate.set(event.value);
    
    // Set end date to end of day today
    const endOfDayToday = new Date(new Date().getTime() + 23 * 60 * 60 * 1000 + 59 * 60 * 1000 + 59 * 1000 + 999);
    this.endDate.set(endOfDayToday);
    
    this.loadDataForGraph();
  }

  onGroupingOptionChange(event: any) {
    this.groupingOption = event.value;

    if (this.groupingOption == GraphGrouping.ByStaffMember) {
      this.selectedStaffMember = null;
    }

    this.loadDataForGraph();
  }

  onForceGraphPeriodChange(checked: boolean) {
    this.forceGraphPeriod.set(checked);
    this.loadDataForGraph();
  }

  onStaffMemberChange(event: any) {
    this.selectedStaffMember = event.value;
    this.loadDataForGraph();
  }

  onSeriesDataLabelOptionChange() {
    this.updateGraph();
  }

  onTrendLineDataLabelOptionChange() {
    this.updateGraph();
  }

  override getDataLabelsConfig(): any {
    const seriesOption = this.seriesDataLabelOption();
    const trendLineOption = this.trendLineDataLabelOption();
    const seriesCount = this.graphData()?.Series?.length ?? 0;
    const hasTrendLine =
      this.displayTrendLine() && seriesCount > 0 && seriesCount <= 2;

    return this.buildSeriesTrendDataLabelsConfig(seriesOption, trendLineOption, {
      hasTrendLine,
      seriesCount: Math.max(seriesCount, 1),
      alternateTrendLines: false,
    });
  }

  getEnabledOnSeries(seriesOption: SeriesDataLabelOption, trendLineOption: TrendLineDataLabelOption): number[] {
    const enabled: number[] = [];
    
    // Series (index 0) - enabled if not LineOnly
    if (seriesOption !== SeriesDataLabelOption.LineOnly) {
      enabled.push(0);
    }
    
    // Trend line (index 1) - enabled if not LineOnly and trend line is displayed
    if (this.displayTrendLine() && trendLineOption !== TrendLineDataLabelOption.LineOnly) {
      enabled.push(1);
    }
    
    return enabled;
  }

  override getMarkersConfig(): any {
    const seriesOption = this.seriesDataLabelOption();
    const trendLineOption = this.trendLineDataLabelOption();
    const series = this.graphData()?.Series ?? [];
    const seriesCount = series.length;
    const hasTrendLine =
      this.displayTrendLine() && seriesCount > 0 && seriesCount <= 2;
    const graphData = this.graphData();
    const seriesDataList = graphData
      ? series.map((s) => this.processSeriesDataWithBreaks(s, graphData).data)
      : [];
    const colors = this.resolveChartColors(series);

    return this.buildSeriesTrendMarkersConfig(seriesOption, trendLineOption, {
      hasTrendLine,
      seriesCount: Math.max(seriesCount, 1),
      alternateTrendLines: false,
      seriesDataList,
      colors,
    });
  }

  shouldDisplayAnnotationLabelBackgroundOption(): boolean {
    return true;
  }

  onAnnotationLabelBackgroundOptionSelected(value: AnnotationLabelBackgroundOption): void {
    this.annotationLabelBackgroundOption.set(value);
    this.onAnnotationLabelBackgroundOptionChange();
  }

  onDeclutterAnnotationsSelected(value: boolean): void {
    this.declutterAnnotations.set(value);
    this.onDeclutterAnnotationsChange();
  }

  onAnnotationLabelPositionChange(
    annotation: GraphAnnotationModel,
    labelPosition: 'start' | 'middle' | 'end'
  ): void {
    this.applyAnnotationLabelPositionChange(
      annotation,
      labelPosition,
      this.graphData(),
      (value) => this.graphData.set(value),
      (updated) => {
        this.graphApiService.SaveGraphAnnotation({ body: updated }).subscribe({
          next: () => this.refreshChartAnnotations(),
          error: (error) =>
            this.alertService.error(
              'Error saving annotation - ' + Utils.getErrorMessageFromErrorResponse(error)
            ),
        });
      }
    );
  }

  getAnnotationListItems() {
    return this.getVisibleAnnotationListItems(this.startDate(), this.endDate());
  }

  onDisplayLegendChange() {
    this.updateGraph();
  }

  onToggleGrouped() {
    this.updateGraph();
  }

  shouldDisplayShowUnusedScaleValues(): boolean {
    return this.selectedGraphType === BehaviorGraphType.IntensityBar;
  }

  filterIntensityBarSeries(data: GraphDataModel): GraphDataModel {
    if (this.selectedGraphType !== BehaviorGraphType.IntensityBar || this.showUnusedScaleValues()) {
      return data;
    }

    return {
      ...data,
      Series: (data.Series ?? []).filter((series) => (series.Value ?? 0) > 0),
    };
  }

  onToggleShowUnusedScaleValues() {
    this.loadDataForGraph();
  }

  onToggleShowNotTrackedAsZeroes() {
    this.loadDataForGraph();
  }

  override updateGraph() {
    if (!this.graphData()) {
      // hide graph
      this.chartOptions.series = [];
      return;
    }

    switch (this.selectedGraphType) {
      case BehaviorGraphType.DurationTotal:
        this.loadDurationTotalGraphOptions(this.graphData()!, this.seriesDataLabelOption(), this.displayLegend(), this.shouldRenderAnnotations(), this.staffMembers());
        break;
      case BehaviorGraphType.Frequency:
      case BehaviorGraphType.DurationAverage:
      case BehaviorGraphType.DurationByTrial:
      case BehaviorGraphType.FrequencyForAntecedents:
      case BehaviorGraphType.FrequencyForConsequences:
      case BehaviorGraphType.FrequencyForLocations:
      case BehaviorGraphType.FrequencyForResponses:
      case BehaviorGraphType.FrequencyForSeverities:
        this.loadSimpleLineForBehaviors();
        break;
      case BehaviorGraphType.TimeOfDay:
        this.loadTimeOfDayGraphOptions(
          this.graphData()!,
          this.shouldRenderAnnotations(),
          this.staffMembers()
        );
        break;
      case BehaviorGraphType.DurationTrialsTimeOfDay:
        this.loadDurationTrialsTimeOfDayGraphOptions();
        break;
      case BehaviorGraphType.AntecedentsForBehavior:
      case BehaviorGraphType.ConsequencesForBehavior:
      case BehaviorGraphType.LocationsForBehavior:
      case BehaviorGraphType.ResponseForBehavior:
      case BehaviorGraphType.SeverityForBehavior:
      case BehaviorGraphType.IntensityBar:
        this.loadBarGraphOptions();
        break;
      case BehaviorGraphType.IntervalPercentageHappened:
      case BehaviorGraphType.IntervalPercentageNotHappened:
        this.loadIntervalPercentageGraphOptions(this.graphData()!, this.displayLegend(), this.shouldRenderAnnotations());
        break;
      case BehaviorGraphType.RateTimerLine:
        this.loadRateTimerLineGraphOptions();
        break;
      case BehaviorGraphType.IntensityOverTime:
        this.loadSimpleLineForBehaviors();
        break;
    }

    // Set a default filename immediately to prevent random filename generation
    this.setDefaultExportFilename();

    // we need to call this so that the options are actually updated on the graph
    this.applyChartOptions(this.chart, this.chartOptions);
  }

  private setDefaultExportFilename(): void {
    // Set a basic filename immediately to prevent ApexCharts from generating a random one
    const graphType = this.getBehaviorGraphTypeDisplayName() || 'Graph';
    const behaviorName = this.selectedBehavior()?.PatientBehaviorName || 'Behavior';
    const defaultFilename = `${graphType}-${behaviorName}`.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '_').substring(0, 100);
    
    if (this.chartOptions.chart?.toolbar?.export) {
      if (!this.chartOptions.chart.toolbar.export.csv) {
        this.chartOptions.chart.toolbar.export.csv = {};
      }
      if (!this.chartOptions.chart.toolbar.export.svg) {
        this.chartOptions.chart.toolbar.export.svg = {};
      }
      if (!this.chartOptions.chart.toolbar.export.png) {
        this.chartOptions.chart.toolbar.export.png = {};
      }
      this.chartOptions.chart.toolbar.export.csv.filename = defaultFilename;
      this.chartOptions.chart.toolbar.export.svg.filename = defaultFilename;
      this.chartOptions.chart.toolbar.export.png.filename = defaultFilename;
    }
  }

  loadIntervalPercentageGraphOptions(graphData: GraphDataModel, displayLegend: boolean, displayAnnotations: boolean) {
    this.chartOptions = {
      series: this.graphData()!.Series!.map((series) => ({
        name: series.Name ?? 'No Name',
        data: this.processSeriesDataWithBreaks(series, graphData).data,
        color: series.Color != null ? series.Color : undefined,
      })),
      legend: {
        position: 'top',
        onItemClick: {
          toggleDataSeries: true,
        },
        onItemHover: {
          highlightDataSeries: true,
        },
      },
      chart: {
        height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
        width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
        type: 'line',
        ...this.sharedChartConfig,
      },
      dataLabels: this.getDataLabelsConfig(),
      stroke: {
        curve: 'straight',
      },
      grid: {
        padding: {
          right: 30,
          left: 20,
        },
      },
      markers: this.getMarkersConfig(),
      tooltip: {
        custom: this.selectedGraphType === BehaviorGraphType.IntervalPercentageHappened ? this.createCustomTooltipIntervalPercentageHappened(this.graphData()!) : this.createCustomTooltipIntervalPercentageDidNotHappen(this.graphData()!)
      },
      title: {
        text: this.graphData()?.Title ?? 'Chart',
      },
      xaxis: {
        type: 'datetime',
        title: {
          text: this.graphData()?.XAxisTitle ?? 'X Axis',
        },
        labels: {
          datetimeUTC: false,
        },
      },
      yaxis: {
        min: 0,
        max: 100,
        title: {
          text: this.graphData()?.YAxisTitle ?? 'Y Axis',
        },
        labels: {
          formatter: (value: number) => {
            // If it's a whole number, don't show decimal places
            if (Number.isInteger(value)) {
              return value.toString();
            }
            // If it's a decimal, round to 2 decimal places
            return value.toFixed(2);
          }
        }
      },
      annotations: this.buildAnnotationsConfig(
        this.graphData()!,
        null,
        this.shouldRenderAnnotations()
      ),
    };
  }

  createCustomTooltipIntervalPercentageHappened(graphData: GraphDataModel) {
    return function({ series, seriesIndex, dataPointIndex, w }: { 
      series: any[], 
      seriesIndex: number, 
      dataPointIndex: number, 
      w: any 
    }) {
      const dataPoint = graphData.Series![seriesIndex].DataPoints![dataPointIndex];
      const totalTrials = dataPoint.RawValue1;
      const totalTrialsHappened = dataPoint.RawValue2;

      const dateToDisplay = new Date(dataPoint.Date ?? new Date().getTime()).toDateString();
      const seriesName = graphData.Series![seriesIndex].Name ?? 'No Name';
      const seriesColor = w.globals.colors[seriesIndex%w.globals.colors.length] ?? '#008FFB';

      if (dataPoint.Value === null || totalTrials === null || totalTrialsHappened === null) {
        return null;
      }

      return (
        '<div class="tooltip-card">' +
        '<div class="tooltip-card-header">' +
        '<h5 class="tooltip-card-title" style="color: ' + seriesColor + ';">' +
        seriesName + '<br>' + '(' + dateToDisplay + ')' +
        '</h5>' +
        '</div>' +
        '<div class="tooltip-card-body">' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Total Trials:</span>' +
        '<span class="tooltip-value">' + totalTrials + '</span>' +
        '</div>' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Total Trials where Behavior Happened:</span>' +
        '<span class="tooltip-value">' + totalTrialsHappened + '</span>' +
        '</div>' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Percentage Calculated:</span>' +
        '<span class="tooltip-value">' + dataPoint.Value + '%</span>' +
        '</div>' +
        '</div>' +
        '</div>'
      );
    };
  }

  createCustomTooltipIntervalPercentageDidNotHappen(graphData: GraphDataModel) {
    return function({ series, seriesIndex, dataPointIndex, w }: { 
      series: any[], 
      seriesIndex: number, 
      dataPointIndex: number, 
      w: any
    }) {
      const dataPoint = graphData.Series![seriesIndex].DataPoints![dataPointIndex];
      const totalTrials = dataPoint.RawValue1;
      const totalTrialsHappened = dataPoint.RawValue2;

      const dateToDisplay = new Date(dataPoint.Date ?? new Date().getTime()).toDateString();
      const seriesName = graphData.Series![seriesIndex].Name ?? 'No Name';
      const seriesColor = w.globals.colors[seriesIndex%w.globals.colors.length] ?? '#008FFB';

      if (dataPoint.Value === null || totalTrials === null || totalTrialsHappened === null) {
        return null;
      }

      return (
        '<div class="tooltip-card">' +
        '<div class="tooltip-card-header">' +
        '<h5 class="tooltip-card-title" style="color: ' + seriesColor + ';">' +
        seriesName + '<br>' + '(' + dateToDisplay + ')' +
        '</h5>' +
        '</div>' +
        '<div class="tooltip-card-body">' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Total Trials:</span>' +
        '<span class="tooltip-value">' + totalTrials + '</span>' +
        '</div>' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Total Trials where Behavior Happened:</span>' +
        '<span class="tooltip-value">' + totalTrialsHappened + '</span>' +
        '</div>' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Percentage Calculated:</span>' +
        '<span class="tooltip-value">' + dataPoint.Value + '%</span>' +
        '</div>' +
        '</div>' +
        '</div>'
      );
    };
  }

  loadBarGraphOptions() {
    this.chartOptions = {
      title: {
        text: this.graphData()?.Title ?? 'Chart',
      },
      series: [
        {
          name: 'count',
          data:
            this.graphData()?.Series!.map((series) => series.Value ?? 0) ?? [],
        },
      ],
      chart: {
        height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
        width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
        type: 'bar',
        events: {
          click: function (chart, w, e) {
            // console.log(chart, w, e)
          },
          mounted: (chartContext: any, config: any) => {
            // Add CSS to make annotation labels more subtle
            const chartElement = chartContext.el;
            const style = document.createElement('style');
            style.textContent = `
                 .apexcharts-annotation-label {
                   z-index: 1 !important;
                   background: rgba(255, 255, 255, 0.1) !important;
                   backdrop-filter: blur(2px) !important;
                   font-size: 14px !important;
                   font-family: monospace !important;
                 }
                 .apexcharts-annotation-text {
                   z-index: 1 !important;
                   opacity: 0.3 !important;
                   font-size: 14px !important;
                   font-family: monospace !important;
                 }
                 .apexcharts-annotations text {
                   z-index: 1 !important;
                   opacity: 0.3 !important;
                   font-size: 14px !important;
                   font-family: monospace !important;
                 }
                 .apexcharts-annotations .apexcharts-annotation-label {
                   z-index: 1 !important;
                   background: rgba(255, 255, 255, 0.1) !important;
                   backdrop-filter: blur(2px) !important;
                   font-size: 14px !important;
                   font-family: monospace !important;
                 }
            `;
            chartElement.appendChild(style);
          }
        },
        ...this.sharedChartConfig,
      },
      colors:
        this.graphData()?.Series!.map((series) => series.Color ?? '#008FFB') ??
        [],
      plotOptions: {
        bar: {
          columnWidth: '45%',
          distributed: true,
        },
      },
      dataLabels: {
        enabled: false,
      },
      legend: {
        show: false,
      },
      grid: {
        show: false,
      },
      xaxis: {
        categories:
          this.graphData()?.Series!.map((series) => series.Name ?? '') ?? [],
        labels: {
          style: {
            fontSize: '12px',
          },
          datetimeUTC: false,
        },
      },
    };
  }

  loadDurationTrialsTimeOfDayGraphOptions() {
    this.chartOptions = {
      // data: [[x, y, size]]
      series: this.graphData()!.Series!.map((series) => ({
        name: series.Name ?? 'No Name',
        data: series.DataPoints!.map(
          (data) =>
            [
              data.Date ? new Date(data.Date).getTime() : new Date().getTime(),
              data.Value,
              data.RawValue1, 
            ] as [number, number, number]
        ),
        color: series.Color ?? '',
      })),
      chart: {
        height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
        width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
        type: "scatter",
        zoom: {
          type: "xy"
        }
      },
      dataLabels: {
        enabled: false
        },
      title: {
        text: this.graphData()?.Title ?? 'Chart',
      },
      grid: {
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
      xaxis: {
        type: 'datetime',
        labels: {
          datetimeUTC: false,
        },
      },
      yaxis: {
        min: (value: number) => {
          if (value < 5 * 3600) {
            return 5 * 3600;
          }
          return value - 1800;
        },
        max: (value: number) => {
          if (value > 20 * 3600) {
            return 20 * 3600;
          }
          return value + 1800;
        },
        labels: {
          formatter: (value: number) => {
            // format time from seconds to HH:MM:SS
            return Utils.formatTime(value);
          },
        },
      },
      tooltip: {
        custom: this.createCustomTooltipDurationTrialsTimeOfDay(this.graphData()!)
      },
      annotations: {
        ...this.buildAnnotationsConfig(this.graphData()!, null, false),
        xaxis: this.shouldRenderAnnotations()
          ? this.xaxisAnnotationsFromGraphData(this.graphData()!)
          : [],
      },
    };
  }

  loadSimpleLineForBehaviors() {
    this.chartOptions = {
      series: this.graphData()!.Series!.map((series, index) =>
        this.mapLineSeriesForChart(series, this.graphData()!, index)
      ),
      colors: this.resolveChartColors(this.graphData()!.Series),
      legend: this.buildLegendConfig(this.displayLegend()),
      chart: {
        height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
        width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
        type: 'line',
        ...this.sharedChartConfig,
      },
      dataLabels: this.getDataLabelsConfig(),
      stroke: {
        curve: 'straight',
      },
      grid: {
        padding: {
          right: 30,
          left: 20,
        },
      },
      markers: this.getMarkersConfig(),
      tooltip: {
        custom: this.createCustomTooltipSimple(this.graphData()!, this.staffMembers())
      },
      title: {
        text: this.graphData()?.Title ?? 'Chart',
      },
      xaxis: {
        type: 'datetime',
        title: {
          text: this.graphData()?.XAxisTitle ?? 'X Axis',
        },
        labels: {
          datetimeUTC: false,
        },
      },
      yaxis: {
        min: 0,
        title: {
          text: this.graphData()?.YAxisTitle ?? 'Y Axis',
        },
        labels: {
          formatter: (value: number) => {
            // If it's a whole number, don't show decimal places
            if (Number.isInteger(value)) {
              return value.toString();
            }
            // If it's a decimal, round to 2 decimal places
            return value.toFixed(2);
          }
        }
      },
      annotations: this.buildAnnotationsConfig(
        this.graphData()!,
        null,
        this.shouldRenderAnnotations()
      ),
    };
  }

  loadRateTimerLineGraphOptions() {
    this.chartOptions = {
      series: this.graphData()!.Series!.map((series) => ({
        name: series.Name ? series.Name : 'No Name',
        data: series.DataPoints!.map(
          (data) =>
            [
              data.Date ? new Date(data.Date).getTime() : new Date().getTime(),
              data.Value,
            ] as [number, number | null]
        ),
      })),
      legend: {
        position: 'top',
        onItemClick: {
          toggleDataSeries: true,
        },
        onItemHover: {
          highlightDataSeries: true,
        },
      },
      chart: {
        height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
        width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
        type: 'line',
        ...this.sharedChartConfig,
      },
      dataLabels: this.getDataLabelsConfig(),
      stroke: {
        curve: 'straight',
      },
      grid: {
        padding: {
          right: 30,
          left: 20,
        },
      },
      markers: this.getMarkersConfig(),
      tooltip: {
        custom: this.createCustomTooltipForRateTimerLine(this.graphData()!, this.staffMembers())
      },
      title: {
        text: this.graphData()?.Title ?? 'Chart',
      },
      xaxis: {
        type: 'datetime',
        title: {
          text: this.graphData()?.XAxisTitle ?? 'X Axis',
        },
        labels: {
          datetimeUTC: false,
        },
      },
      yaxis: {
        min: 0,
        title: {
          text: this.graphData()?.YAxisTitle ?? 'Y Axis',
        },
        labels: {
          formatter: (value: number) => {
            // If it's a whole number, don't show decimal places
            if (Number.isInteger(value)) {
              return value.toString();
            }
            // If it's a decimal, round to 2 decimal places
            return value.toFixed(2);
          }
        }
      },
      annotations: this.buildAnnotationsConfig(
        this.graphData()!,
        null,
        this.shouldRenderAnnotations()
      ),
    };
  }

  createCustomTooltipForRateTimerLine(graphData: GraphDataModel, staffMembers: DataTakerStaffDataModel[] = []) {
    return function({ series, seriesIndex, dataPointIndex, w }: { 
      series: any[], 
      seriesIndex: number, 
      dataPointIndex: number, 
      w: any 
    }) {
      // should show the date and the rate of the target
      const dataPoint = graphData.Series![seriesIndex].DataPoints![dataPointIndex];
      // the date should be in the format of "MMM dd yyyy - HH:MM:SS"
      const dateToDisplay = new Date(dataPoint.Date ?? new Date().getTime()).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
      const rate = dataPoint.Value;
      const seriesName = graphData.Series![seriesIndex].Name ?? 'No Name';
      const seriesColor = w.globals.colors[seriesIndex%w.globals.colors.length] ?? '#008FFB';

      return '<div class="tooltip-card">' +
        '<div class="tooltip-card-header">' +
        '<h5 class="tooltip-card-title" style="color: ' + seriesColor + ';">' +
        seriesName +
        '</h5>' +
        '</div>' +
        '<div class="tooltip-card-body">' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Date:</span>' +
        '<span class="tooltip-value">' + dateToDisplay + '</span>' +
        '</div>' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Rate:</span>' +
        '<span class="tooltip-value">' + rate + ' times' + '</span>' +
        '</div>' +
        '</div>' +
        '</div>'
      ;
    };
  }

  createCustomTooltipDurationTrialsTimeOfDay(graphData: GraphDataModel, staffMembers: DataTakerStaffDataModel[] = []) {
    return function({ series, seriesIndex, dataPointIndex, w }: { 
      series: any[], 
      seriesIndex: number, 
      dataPointIndex: number, 
      w: any 
    }) {
      // should show the date and the duration of the target in minutes.
      const dataPoint = graphData.Series![seriesIndex].DataPoints![dataPointIndex];
      // the date should be in the format of "MMM dd yyyy - HH:MM:SS"
      const dateToDisplay = new Date(dataPoint.Date ?? new Date().getTime()).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
      // time from the day start in seconds
      const timeFromDayStart = dataPoint.Value ?? 0;
      // time calculated from the day start
      const timeFromDayStartFormatted = Utils.formatTime(timeFromDayStart);
      const duration = dataPoint.RawValue1;
      const seriesName = graphData.Series![seriesIndex].Name ?? 'No Name';
      const seriesColor = w.globals.colors[seriesIndex%w.globals.colors.length] ?? '#008FFB';

      // create string of staff members names separated by commas
      const staffIDs = dataPoint.StaffIDs;
      const staffsForDataPoint = staffMembers.filter((staffMember) => staffIDs?.includes(staffMember.StaffID!));
      const staffMembersToDisplay = staffsForDataPoint.map((staffMember) => staffMember.FullName || 'No User').join(', ');
      const finalStaffDisplay = staffMembersToDisplay || 'No User';
      const staffMembersHTML = '<div class="tooltip-item">' +
        '<span class="tooltip-label">Staff Members:</span>' +
        '<span class="tooltip-value">' + finalStaffDisplay + '</span>' +
        '</div>';
      
      return '<div class="tooltip-card">' +
        '<div class="tooltip-card-header">' +
        '<h5 class="tooltip-card-title" style="color: ' + seriesColor + ';">' +
        seriesName +
        '</h5>' +
        '</div>' +
        '<div class="tooltip-card-body">' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Date:</span>' +
        '<span class="tooltip-value">' + dateToDisplay + '</span>' +
        '</div>' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Duration (in Minutes):</span>' +
        '<span class="tooltip-value">' + duration + ' minutes' + '</span>' +
        '</div>' +
        staffMembersHTML +
        // '<div class="tooltip-item">' +
        // '<span class="tooltip-label">Time from Day Start:</span>' +
        // '<span class="tooltip-value">' + timeFromDayStart + ' seconds' + '</span>' +
        // '</div>' +
        // '<div class="tooltip-item">' +
        // '<span class="tooltip-label">Time from Day Start (Formatted):</span>' +
        // '<span class="tooltip-value">' + timeFromDayStartFormatted + '</span>' +
        // '</div>' +
        '</div>' +
        '</div>'
      ;
    };
  }

  // Annotation functions

  addAnnotationButtonClicked() {
    console.log('addAnnotationButtonClicked');
    if (this.selectedBehavior() != null) {
    const emptyAnnotation: GraphAnnotationModel = {
        PatientBehaviorIDs: [this.selectedBehavior()!.ID!],
      };
      this.annotationToEdit.set(emptyAnnotation);
    }
    this.showAddAnnotation.set(true);
  }

  onToggleTrendLine() {
    this.loadDataForGraph();
  }

  onToggleSegmentTrendLineByPhase() {
    this.loadDataForGraph();
  }

  editAnnotation(annotation: GraphAnnotationModel) {
    console.log('editAnnotation', annotation);
    this.annotationToEdit.set(annotation);
    this.showAddAnnotation.set(true);
  }

  onCloseAddAnnotation(withChanges: boolean = false) {
    console.log('onCloseAddAnnotation', withChanges);
    if (withChanges) {
      this.loadData();
    }
    this.showAddAnnotation.set(false);
  }

  /**
   * Formats annotation label text to use multiple lines if it exceeds 30 characters
   */
  protected override formatAnnotationLabelText(text: string): string | string[] {
    if (text.length <= 30) {
      return text;
    }

    // Split the text into words and filter out empty strings
    const words = text.split(' ').filter(word => word.length > 0);
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      // If adding this word would exceed 30 characters, start a new line
      if (currentLine.length + word.length + 1 > 30 && currentLine.length > 0) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        if (currentLine.length === 0) {
          currentLine = word;
        } else {
          currentLine += ' ' + word;
        }
      }
    }

    // Add the last line if it has content
    if (currentLine.length > 0) {
      lines.push(currentLine);
    }

    // Return array of strings for multiline support in ApexCharts
    // Pad each line to exactly 30 characters with whitespace
    if (lines.length > 1) {
      return lines.map(line => line.padEnd(30, ' '));
    }
    
    return text;
  }

  /**
   * Handles print click from the graph toolbar
   */
  override handleDownloadPngClick(): void {
    if (!this.graphData()) return;
    this.buildReportHeaderInfo().then((header) => {
      const isGrouped = this.isGrouped();
      if (isGrouped) {
        if (!this.chart) return;
        this.printService.downloadPngPage(this.chart as any, header);
      } else {
        const charts = this.chartList ? this.chartList.toArray() : [];
        if (charts.length === 0) return;
        this.printService.downloadPngPageForCharts(charts as any, header);
      }
    });
  }

  override handleDownloadPdfClick(): void {
    if (!this.graphData()) return;
    this.buildHeaderInfoAndDownloadPdf();
  }

  private async buildReportHeaderInfo(): Promise<PrintHeaderInfo> {
    let patientName = 'Unknown Patient';
    try {
      const patient = await this.staffApiService.GetPatientInfo({ id: Number(this.patientID()) }).pipe(take(1)).toPromise();
      if (patient) {
        patientName = `${patient.FirstName || ''} ${patient.LastName || ''}`.trim() || patient.PatientName || 'Unknown Patient';
      }
    } catch {}
    const startDate = this.startDate();
    const endDate = this.endDate();
    const timePeriod = `${this.datePipe.transform(startDate, 'MMM dd, yyyy')} - ${this.datePipe.transform(endDate, 'MMM dd, yyyy')}`;
    const graphType = this.getBehaviorGraphTypeDisplayName();
    const subtitleItems = this.selectedBehavior() ? [`${this.selectedBehavior()!.PatientBehaviorName || 'Unnamed Behavior'} (${Utils.patientBehaviorTypeLabel(this.selectedBehavior()!.BehaviorType!)})`] : [];
    const selectedBehaviors = this.selectedBehavior() ? [this.selectedBehavior()!.PatientBehaviorName || 'Unknown Behavior'] : [];
    const visibleAnnotations = this.annotations()
      .filter(annotation => {
        if (!this.isAnnotationDisplayed(annotation)) return false;
        if (annotation.StartDate) {
          const annotationDate = new Date(annotation.StartDate);
          return annotationDate >= startDate && annotationDate <= endDate;
        }
        return true;
      })
      .map(annotation => (this.annotationLabel(annotation)));
    const headerInfo: PrintHeaderInfo = {
      graphTitle: this.graphData()?.Title || 'Behavior Graph',
      graphType,
      componentType: 'Behavior Graph',
      patientName,
      timePeriod,
      groupingLabel: this.groupingOptionLabel(),
      displayOptions: this.getDisplayOptions(),
      subtitleItems,
      selectedBehaviors,
      selectedBehavior: this.selectedBehavior()?.PatientBehaviorName || undefined,
      annotations: visibleAnnotations,
      annotationColorLegends: this.getDeclutterLegendItemsForExport(),
      staffMember: this.getStaffMember() ?? undefined
    };
    return headerInfo;
  }

  private groupingOptionLabel(): string {
    const groupingMap: { [key in GraphGrouping]: string } = {
      [GraphGrouping.BySession]: 'By Session',
      [GraphGrouping.Daily]: 'Daily',
      [GraphGrouping.Weekly]: 'Weekly',
      [GraphGrouping.Monthly]: 'Monthly',
      [GraphGrouping.Yearly]: 'Yearly',
      [GraphGrouping.ByStaffMember]: 'By Staff Member',
      [GraphGrouping.ByTrial]: 'By Trial',
    };
    return groupingMap[this.groupingOption];
  }

  private getDisplayOptions(): string[] {
    const options: string[] = [];
    if (this.shouldDisplayToggleGrouped()) {
      if (this.isGrouped()) options.push('Grouped'); else options.push('Synced');
    }

    // not tracked as zeroes option
    if (this.shouldDisplayShowNotTrackedAsZeroes()) {
      if (this.showNotTrackedAsZeroes()) options.push('Not Tracked as Zeroes');
    }

    options.push(`Series: ${this.seriesDataLabelOption()}`);
    if (this.displayTrendLine()) {
      options.push(`Trend Line: ${this.trendLineDataLabelOption()}`);
    }
    if (this.displayAverage() && this.hasAverageAnnotations()) {
      options.push('Average');
    }
    if (this.shouldDisplayAnnotationLabelBackgroundOption()) {
      options.push(`Annotation Labels: ${this.annotationLabelBackgroundOption()}`);
    }
    if (this.declutterAnnotations()) {
      options.push('Declutter Annotations');
    }

    return options;
  }

  private getStaffMember(): string | undefined {
    return this.selectedStaffMember?.FullName ?? undefined;
  }

  private async buildHeaderInfoAndDownloadPdf(): Promise<void> {
    this.isPrintingPdf = true;
    this.updateGraph();
    const headerInfo: PrintHeaderInfo = await this.buildReportHeaderInfo();
    const isGrouped = this.isGrouped();
    if (isGrouped) {
      if (!this.chart) return;
      this.printService.downloadPdf(this.chart as any, headerInfo);
    } else {
      const charts = this.chartList ? this.chartList.toArray() : [];
      if (charts.length === 0) return;
      this.printService.downloadPdfForCharts(charts as any, headerInfo);
    }
    this.isPrintingPdf = false;
    this.updateGraph();
  }

  /**
   * Gets the display name for the current behavior graph type
   */
  private getBehaviorGraphTypeDisplayName(): string {
    if (this.selectedGraphType === null || this.selectedGraphType === undefined) {
      return 'Unknown Graph Type';
    }

    const graphTypeMap: { [key in BehaviorGraphType]: string } = {
      [BehaviorGraphType.Frequency]: 'Frequency',
      [BehaviorGraphType.DurationTotal]: 'Duration Total',
      [BehaviorGraphType.DurationAverage]: 'Duration Average',
      [BehaviorGraphType.TimeOfDay]: 'Time of Day',
      [BehaviorGraphType.AntecedentsForBehavior]: 'Antecedents for Behavior',
      [BehaviorGraphType.ConsequencesForBehavior]: 'Consequences for Behavior',
      [BehaviorGraphType.LocationsForBehavior]: 'Locations for Behavior',
      [BehaviorGraphType.ResponseForBehavior]: 'Response for Behavior',
      [BehaviorGraphType.SeverityForBehavior]: 'Severity for Behavior',
      [BehaviorGraphType.FrequencyForAntecedents]: 'Frequency for Antecedents',
      [BehaviorGraphType.FrequencyForConsequences]: 'Frequency for Consequences',
      [BehaviorGraphType.FrequencyForLocations]: 'Frequency for Locations',
      [BehaviorGraphType.FrequencyForResponses]: 'Frequency for Responses',
      [BehaviorGraphType.FrequencyForSeverities]: 'Frequency for Severities',
      [BehaviorGraphType.IntervalPercentageHappened]: 'Interval Percentage Happened',
      [BehaviorGraphType.IntervalPercentageNotHappened]: 'Interval Percentage Not Happened',
      [BehaviorGraphType.DurationByTrial]: 'Duration By Trial',
      [BehaviorGraphType.DurationTrialsTimeOfDay]: 'Duration Trials - Time of Day',
      [BehaviorGraphType.RateTimerLine]: 'Rate Timer Line',
      [BehaviorGraphType.IntensityBar]: 'Intensity',
      [BehaviorGraphType.IntensityOverTime]: 'Intensity Over Time'
    };

    return graphTypeMap[this.selectedGraphType] || 'Unknown Graph Type';
  }

}
